<template>
  <div class="login-page full-height columns gap2">
    <div class="span-5 p-4 text-center flex fd-c jc-c">
      <h1 class="mb-1 text-xg">Acesso do Portal</h1>
      <p class="mb-3">Digite o seu email e senha para acessar</p>

      <form class="flex fd-c fgap2" @submit.prevent="login">
        <Input
          v-model="userData.email"
          :required="true"
          className="input-login"
          label="Email"
          name="email"
          placeholder="Seu email"
          type="email"
        />

        <Input
          v-model="userData.senha"
          :required="true"
          className="input-login"
          label="Senha"
          name="senha"
          placeholder="Sua senha"
          type="password"
        />

        <label class="remember-row flex ai-c fgap1 text-left">
          <input
            v-model="rememberCredentials"
            type="checkbox"
            class="remember-checkbox"
          />
          <span class="remember-label">Lembrar usuário e senha neste dispositivo</span>
        </label>

        <div class="pv-1 text-right">
          <router-link :to="{name: 'ForgotPassword'}">
            Esqueceu sua senha?

          </router-link>
        </div>

        <button class="btn solid primary full-width extra-big btn-auth" type="submit">ACESSAR</button>
        <div v-if="loading">carregando</div>
      </form>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
// eslint-disable-next-line import/no-cycle
import api from '@/services/api';
import Input from '@/components/DataInput/Input.vue';
import {
  LS_LOGIN_REMEMBER,
  LS_LOGIN_USERNAME,
  LS_LOGIN_PASSWORD_ENC,
} from '@/utils/loginRememberStorage';

const LOGIN_REMEMBER_PEPPER = 'GescomexPortalParceirosLoginRemember-v1';

function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

function base64ToArrayBuffer(b64) {
  const binary = window.atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

export default {
  name: 'Login',
  components: {
    Input,
  },
  computed: {
    ...mapState(['loading']),
  },
  data() {
    return {
      userData: {
        email: '',
        senha: '',
      },
      rememberCredentials: false,
    };
  },
  mounted() {
    this.loadRememberedLogin();
  },
  methods: {
    ...mapActions(['toggleLoading']),
    ...mapActions('auth', ['updateUser']),
    clearRememberedLogin() {
      window.localStorage.removeItem(LS_LOGIN_REMEMBER);
      window.localStorage.removeItem(LS_LOGIN_USERNAME);
      window.localStorage.removeItem(LS_LOGIN_PASSWORD_ENC);
    },
    async getRememberAesKey() {
      if (!window.crypto || !window.crypto.subtle) {
        return null;
      }
      const enc = new TextEncoder();
      const host = typeof window !== 'undefined' ? window.location.host : '';
      const material = enc.encode(`${LOGIN_REMEMBER_PEPPER}|${host}`);
      const hash = await window.crypto.subtle.digest('SHA-256', material);
      return window.crypto.subtle.importKey(
        'raw',
        hash,
        { name: 'AES-GCM' },
        false,
        ['encrypt', 'decrypt'],
      );
    },
    async encryptPasswordForStorage(plainPassword) {
      const key = await this.getRememberAesKey();
      if (!key) {
        throw new Error('Web Crypto indisponível');
      }
      const iv = window.crypto.getRandomValues(new Uint8Array(12));
      const enc = new TextEncoder();
      const ciphertext = await window.crypto.subtle.encrypt(
        { name: 'AES-GCM', iv },
        key,
        enc.encode(plainPassword),
      );
      return JSON.stringify({
        v: 1,
        iv: arrayBufferToBase64(iv),
        ct: arrayBufferToBase64(ciphertext),
      });
    },
    async decryptPasswordFromStorage(jsonStr) {
      const key = await this.getRememberAesKey();
      if (!key || !jsonStr) {
        return null;
      }
      const parsed = JSON.parse(jsonStr);
      if (parsed.v !== 1 || !parsed.iv || !parsed.ct) {
        return null;
      }
      const iv = new Uint8Array(base64ToArrayBuffer(parsed.iv));
      const ct = base64ToArrayBuffer(parsed.ct);
      const plainBuf = await window.crypto.subtle.decrypt(
        { name: 'AES-GCM', iv },
        key,
        ct,
      );
      return new TextDecoder().decode(plainBuf);
    },
    async persistRememberedLoginAfterSuccess() {
      if (!this.rememberCredentials) {
        this.clearRememberedLogin();
        return;
      }
      if (!this.userData.email || !this.userData.senha) {
        return;
      }
      try {
        const encPayload = await this.encryptPasswordForStorage(this.userData.senha);
        window.localStorage.setItem(LS_LOGIN_REMEMBER, 'true');
        window.localStorage.setItem(LS_LOGIN_USERNAME, this.userData.email);
        window.localStorage.setItem(LS_LOGIN_PASSWORD_ENC, encPayload);
      } catch (e) {
        this.clearRememberedLogin();
      }
    },
    async loadRememberedLogin() {
      if (window.localStorage.getItem(LS_LOGIN_REMEMBER) !== 'true') {
        return;
      }
      this.rememberCredentials = true;
      const savedUser = window.localStorage.getItem(LS_LOGIN_USERNAME);
      const enc = window.localStorage.getItem(LS_LOGIN_PASSWORD_ENC);
      if (savedUser) {
        this.userData.email = savedUser;
      }
      if (!enc) {
        return;
      }
      try {
        const pwd = await this.decryptPasswordFromStorage(enc);
        if (pwd) {
          this.userData.senha = pwd;
        }
      } catch (e) {
        this.clearRememberedLogin();
        this.rememberCredentials = false;
        this.userData.senha = '';
      }
    },
    async login() {
      this.toggleLoading(true);
      try {
        const params = new URLSearchParams();
        params.append('grant_type', 'password');
        params.append('username', this.userData.email);
        params.append('password', this.userData.senha);

        await api.post('/oauth/token', params, {
          auth: {
            username: process.env.VUE_APP_API_USER,
            password: process.env.VUE_APP_API_PWD,
          },
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        });

        const userResponse = await api.get('/credential');
        await this.persistRememberedLoginAfterSuccess();
        this.updateUser(userResponse.data.data);

        this.$router.push('/');
      } catch (err) {
        if (err?.status === 400) {
          this.$toast.error('Email ou senha inválidos!');
        } else {
          this.$toast.error('Não foi possível executar esta ação no momento!');
        }
      }
      this.toggleLoading(false);
    },
  },
};
</script>

<style scoped>
.login-page {
  background: url("../../assets/img/bg-login.jpg") right center / auto 100% no-repeat, var(--white-color);
}

form {
  max-width: 500px;
  width: 100%;
  margin: 0 auto;
}

@media screen and (max-width: 1300px) {
  .login-page.columns {
    grid-template-columns: 1fr;
    background: var(--white-color);
  }
}

.remember-row {
  cursor: pointer;
  user-select: none;
  margin-top: -0.25rem;
}

.remember-checkbox {
  width: 1.125rem;
  height: 1.125rem;
  flex-shrink: 0;
  accent-color: var(--primary-color, #2563eb);
  cursor: pointer;
}

.remember-label {
  font-size: 0.875rem;
  line-height: 1.4;
  color: var(--text-color, #444);
}

.btn.solid.primary.btn-auth {
  background-color: #161635;
  border-color: #161635;
  color: #fff;
}

.btn.solid.primary.btn-auth:hover {
  background-color: transparent;
  border-color: #161635;
  color: #161635;
}
</style>
